import { useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import diagnosticsImg from "@/assets/diagnostics.jpg";
import brakesImg from "@/assets/brakes.jpg";
import roadsideImg from "@/assets/roadside.jpg";
import inspectionImg from "@/assets/inspection.jpg";
import oilChangeImg from "@/assets/gallery-oil-change.jpg";
import sparkplugsImg from "@/assets/gallery-sparkplugs.jpg";
import tireImg from "@/assets/gallery-tire.jpg";
import batteryImg from "@/assets/gallery-battery.jpg";
import workshopImg from "@/assets/workshop.png";
import engineBayImg from "@/assets/engine-bay.png";

const galleryImages = [
  { src: workshopImg, alt: "Professional automotive workshop", category: "Workshop" },
  { src: engineBayImg, alt: "Engine bay close-up detail", category: "Engine" },
  { src: diagnosticsImg, alt: "Engine diagnostics in progress", category: "Diagnostics" },
  { src: oilChangeImg, alt: "Engine oil change service", category: "Maintenance" },
  { src: brakesImg, alt: "Brake and suspension work", category: "Brakes" },
  { src: sparkplugsImg, alt: "Spark plug replacement", category: "Engine" },
  { src: roadsideImg, alt: "Roadside recovery service", category: "Recovery" },
  { src: tireImg, alt: "Tire change on location", category: "Tires" },
  { src: inspectionImg, alt: "Pre-purchase vehicle inspection", category: "Inspection" },
  { src: batteryImg, alt: "Battery testing and replacement", category: "Electrical" },
];

const Gallery = () => {
  const [lightbox, setLightbox] = useState<number | null>(null);

  const navigate = (dir: number) => {
    if (lightbox === null) return;
    const next = (lightbox + dir + galleryImages.length) % galleryImages.length;
    setLightbox(next);
  };

  return (
    <>
      <PageHeader title="Our Gallery" subtitle="Real work, real results — see our recent jobs" breadcrumb="Gallery" />

      <section className="section-padding bg-background">
        <div className="container mx-auto">
          {/* Masonry-style grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {galleryImages.map((img, i) => (
              <button
                key={i}
                onClick={() => setLightbox(i)}
                className={`overflow-hidden rounded-xl group relative cursor-pointer shadow-card-premium hover:shadow-elevated transition-all duration-500 hover:-translate-y-1 ${
                  i === 0 ? "sm:col-span-2 sm:row-span-2 aspect-square" : "aspect-square"
                }`}
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-secondary/80 via-secondary/0 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end justify-start p-3 sm:p-4">
                  <span className="font-heading text-xs sm:text-sm uppercase tracking-wider text-primary-foreground translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
                    {img.category}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Premium Lightbox */}
      {lightbox !== null && (
        <div className="fixed inset-0 z-[100] bg-secondary/98 backdrop-blur-xl flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          {/* Close */}
          <button className="absolute top-4 right-4 sm:top-6 sm:right-6 text-primary-foreground/70 hover:text-primary transition-colors p-2" onClick={() => setLightbox(null)}>
            <X className="h-6 w-6 sm:h-8 sm:w-8" />
          </button>
          
          {/* Navigation */}
          <button
            className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 text-primary-foreground/50 hover:text-primary transition-colors p-2"
            onClick={(e) => { e.stopPropagation(); navigate(-1); }}
          >
            <ChevronLeft className="h-8 w-8 sm:h-10 sm:w-10" />
          </button>
          <button
            className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 text-primary-foreground/50 hover:text-primary transition-colors p-2"
            onClick={(e) => { e.stopPropagation(); navigate(1); }}
          >
            <ChevronRight className="h-8 w-8 sm:h-10 sm:w-10" />
          </button>

          {/* Image */}
          <div className="animate-scale-in" onClick={(e) => e.stopPropagation()}>
            <img
              src={galleryImages[lightbox].src}
              alt={galleryImages[lightbox].alt}
              className="max-w-full max-h-[80vh] object-contain rounded-xl shadow-2xl"
            />
            <p className="text-center mt-4 font-heading text-sm uppercase tracking-wider text-primary">
              {galleryImages[lightbox].category}
            </p>
          </div>

          {/* Counter */}
          <div className="absolute bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 font-heading text-xs text-silver/50 uppercase tracking-widest">
            {lightbox + 1} / {galleryImages.length}
          </div>
        </div>
      )}
    </>
  );
};

export default Gallery;
